import { MenuIcon, XIcon } from '@heroicons/react/outline'
import { Variants, motion, useScroll } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CurrentUser } from '../../../../../model/Common/common.interface'
import { STUDENT_BTN_ID } from '../../../../../modules/ids/gtm/student'
import { ASIA_SEOUL, COOKIE_OPT } from '../../../../../modules/vars'
import CookieBanner from '../../../../banner/CookieBanner'
import EmptyBlock from '../../../../common/EmptyBlock'
import { IMG_NAVIGATION_RINGLE_LOGO } from '../../../constants/navigation.constant'
/**
 * for Safari browser below 13.1
 * https://ringleplus.sentry.io/issues/3759888820/events/7370260621964a91be34541697b12a76/?project=6093896
 * https://stackoverflow.com/questions/65819296/unhandled-runtime-error-referenceerror-cant-find-variable-resizeobserver-on-s
 * https://github.com/nerdyman/react-compare-slider/issues/27
 */
import { COLORS } from '@repo/design-system/index'
import { QIcon_close_default } from '@repo/icons/index'
import { useCookies } from 'react-cookie'
import { install } from 'resize-observer'
import { sendGTMEvent } from '../../../../../helpers/gtmHelper'
import { useTrialStatusSummary } from '../../../../../modules/apis/student/trial'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import { useShowChannelButton } from '../../../../../modules/hooks/common/useChannelIOSetup'
import useHandleLogout from '../../../../../modules/hooks/student/auth/useHandleLogout'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import { useSidePopup } from '../../../../basic/popup/SidePopup'
import BandBannerContainer from '../../../../common/BandBannerContainer'
import { sendNavigationMenuClickEvent } from '../../../helpers/NavigationHelpers'
import HeaderAuthButton from './HeaderAuthButton'
import HeaderNavigationInnerMenu from './HeaderNavigationInnerMenu'

if (typeof window !== 'undefined') install()

const variants: Variants = {
  visible: {
    visibility: 'visible',
    opacity: 1,
  },
  hidden: {
    opacity: 0,
    transitionEnd: {
      visibility: 'hidden',
    },
  },
}

interface IHeaderNavigationBarV2Mobile {
  channelTalk?: boolean
  subNavTabClassName?: string
  subNavTab?: any
}
const HeaderNavigationBarV2Mobile = ({
  channelTalk = true,
  subNavTabClassName = '',
  subNavTab = <></>,
}: IHeaderNavigationBarV2Mobile) => {
  const router = useAppRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const handleLogout = useHandleLogout()
  useShowChannelButton(channelTalk)

  const { t, isLocaleKO } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { scrollY } = useScroll()
  const sidePopup = useSidePopup()

  const [barState, setBarState] = useState('visible')
  const [curScrollY, setCurScrollY] = useState(scrollY.get())
  const [cookies, setCookies] = useCookies(['is_close_cookie_banner'])

  const [containerHeight, setContainerHeight] = useState(58)
  const [openMenu, setOpenMenu] = useState<boolean>(false)

  const isLanding = useMemo(() => router.asPath.includes('/landing/'), [router.asPath])
  const isLandingHome = useMemo(() => router.asPath.includes(urls.oneOnOne.index), [router.asPath])

  const isSignInPage = useMemo(() => router.asPath.includes(urls.student.auth.signIn), [router.asPath])
  const isSignUpPage = useMemo(
    () =>
      router.asPath.includes(urls.student.auth.signUp) ||
      router.asPath.includes(urls.student.auth.new) ||
      router.asPath.includes(urls.student.auth.additionalInfo),
    [router.asPath]
  )
  const isWelcomePage = useMemo(() => router.asPath.includes(urls.student.newPortal.welcome), [router.asPath])
  const isTrialCompletePage = useMemo(() => router.asPath.includes('/student/portal/trial/complete'), [router.asPath])

  const { data } = useTrialStatusSummary()

  const handleClickLogo = () => {
    sendNavigationMenuClickEvent(STUDENT_BTN_ID.CLICK_HOME_RINGLE_LOGO, 'Ringle Logo', currentUser, router, null)

    if (isLandingHome) {
      router.reload()
    } else {
      router.push(urls.oneOnOne.index)
    }
  }

  const handleOnClickPurchase = (_currentUser: CurrentUser) => {
    sendGTMEvent(STUDENT_BTN_ID.CLICK_GNB_PURCHASE, {
      btn_name: 'purchase',
      device_type: 'mobile_web',
      custom_page_title: isTrialCompletePage ? 'trial_complete' : 'trial_welcome',
    })
    router.push(urls.student.portal.pricing.index)
  }

  const handleOnClickAuthBtn = (_currentUser: CurrentUser) => {
    sendNavigationMenuClickEvent(
      _currentUser ? STUDENT_BTN_ID.CLICK_NAVIGATION_LOGOUT : STUDENT_BTN_ID.CLICK_NAVIGATION_LOGIN,
      _currentUser ? '로그아웃' : '무료체험',
      currentUser,
      router,
      null
    )

    if (_currentUser) {
      handleLogout()
    } else {
      isSignUpPage ? router.push(urls.student.auth.signIn) : router.push(urls.student.auth.new)
    }
  }

  const handleOnClickSignUpBtn = () => {
    sendNavigationMenuClickEvent(STUDENT_BTN_ID.SIGN_UP, '회원가입', currentUser, router, null)
    router.push(urls.student.auth.new)
  }

  const handleCloseCookieBanner = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setCookies('is_close_cookie_banner', true, COOKIE_OPT)
  }

  const handleGNBMenuButton = () => {
    sidePopup.show({
      id: 'menu_mobile_landing',
      hFull: true,
      noTopSide: true,
      gapFromTop: 0,
      maxW: 264,
      body: (
        <div className="">
          <div className="px-[20px] py-[10px] flex flex-row items-center justify-between">
            <HeaderAuthButton
              key={'first'}
              isSignUpPage={isSignUpPage}
              isSignInPage={isSignInPage}
              isWelcomePage={isWelcomePage}
              data={data}
              isTrialCompletePage={isTrialCompletePage}
              handleOnClickPurchase={handleOnClickPurchase}
              handleOnClickAuthBtn={handleOnClickAuthBtn}
              handleOnClickSignUpBtn={handleOnClickSignUpBtn}
            />
            {/* 모바일 닫기 버튼 */}
            <div className="cursor-pointer" onClick={() => sidePopup.close('menu_mobile_landing')}>
              <QIcon_close_default width={24} height={24} color={COLORS.BLACK} />
            </div>
          </div>
          <HeaderNavigationInnerMenu />
        </div>
      ),
    })
  }
  useEffect(() => {
    /**
     * <쿠키 배너 노출 기준>
     * 로그인 유저 : timezone이 한국이 아닐 경우 노출
     * 미로그인 유저: 언어가 영어이면 노출
     *
     * 위 케이스 외에는 쿠키 배너 노출하지 않도록  is_close_cookie_banner 쿠키에 저장
     * + close 버튼을 눌러서 쿠키배너를 닫은 경우에도 is_close_cookie_banner 쿠키에 저장
     */
    if ((currentUser && currentUser?.timezone === ASIA_SEOUL) || (!currentUser && isLocaleKO)) {
      setCookies('is_close_cookie_banner', true, COOKIE_OPT)
    }
  }, [currentUser?.timezone])

  useEffect(() => {
    return scrollY.onChange((latest) => {
      // For top bouncing in iOS
      if (latest <= 0) {
        setBarState('visible')
        setCurScrollY(0)
        return
      }

      if (latest > curScrollY) {
        setBarState('hidden')
      } else {
        setBarState('visible')
      }

      setCurScrollY(latest)
    })
  }, [scrollY, curScrollY])

  useEffect(() => {
    const containerResizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const element = entries[0]
        setContainerHeight(element.contentRect.height)
      }
    })

    if (containerRef && containerRef.current) {
      containerResizeObserver.observe(containerRef.current)
    }

    return () => containerResizeObserver.disconnect()
  }, [containerRef])

  return (
    <nav>
      <motion.div
        ref={containerRef}
        className="fixed z-[1000] w-full backdrop-blur-[5px]"
        initial="visible"
        animate={barState}
        variants={variants}
      >
        {/* Navigation Bar */}
        <BandBannerContainer />
        <div className="flex min-h-[40px] w-full items-center justify-between bg-[rgba(255,255,255,0.85)] py-[11px] px-[20px] backdrop-blur-[10px]">
          <div className="flex items-center justify-start w-full">
            <button onClick={() => handleClickLogo()}>
              <img src={IMG_NAVIGATION_RINGLE_LOGO} alt="ringle-logo" />
            </button>
          </div>
          <div className="flex items-center justify-end w-full">
            <HeaderAuthButton
              key={'second'}
              isSignUpPage={isSignUpPage}
              isSignInPage={isSignInPage}
              isWelcomePage={isWelcomePage}
              data={data}
              isTrialCompletePage={isTrialCompletePage}
              handleOnClickPurchase={handleOnClickPurchase}
              handleOnClickAuthBtn={handleOnClickAuthBtn}
              handleOnClickSignUpBtn={handleOnClickSignUpBtn}
            />

            <div className="ml-[16px]">
              {/* 모바일 닫기 버튼 */}
              {openMenu ? (
                <XIcon className="z-50 block h-20 w-20" aria-hidden="true" />
              ) : (
                <MenuIcon
                  onClick={() => handleGNBMenuButton()}
                  className="block h-20 w-20 text-black"
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
        </div>

        <div className={subNavTabClassName}>{subNavTab}</div>
      </motion.div>

      <EmptyBlock h={containerHeight} />

      {cookies.is_close_cookie_banner !== 'true' && isLanding && <CookieBanner handleClose={handleCloseCookieBanner} />}
    </nav>
  )
}

export default HeaderNavigationBarV2Mobile
